import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {mergeRegister} from '@lexical/utils';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import {
  $createListItemNode,
    $createListNode,
    $isListItemNode,
    $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND
} from '@lexical/list'
import {useCallback, useEffect, useRef, useState} from 'react';
import EmojiPlugin from './EmojiPlugin';
import IconWrapper from '../../Cards/IconWrapper';
import { AlignCenter, AlignLeft, AlignRight, Bold, Eraser, Italic, List, ListOrdered, Plus, Underline } from 'lucide-react';
import LinkPlugin from './LinkPlugin';
import { $isLinkNode } from '@lexical/link';
import CustomToolTip from '../../Tooltip/CustomToolTip';
import Modal from '../../Modals/Modal';
import { InputField } from '../../Inputs/InputField';
import GlobalDropDown from '../../Dropdowns/GlobalDropDown';
import { showErrorToast } from '../../ui/Toast';

const LowPriority = 1;

function ToolButton({Icon,command,commandType,isActive}){
  const [editor] = useLexicalComposerContext();

    return (
    <button
        type='button'
        onClick={() => {
          editor.dispatchCommand(command, commandType);
        }}
        className={'m-1 hover-outline rounded-xl ' + (isActive ? 'bg-accent-300' : '')}
        aria-label="Format Bold">
            {Icon()}
      </button>
      )
}

function ListToolButton({Icon,isActive,isNumberedList}){
    const [editor] = useLexicalComposerContext();

    const handleRemoveList = ()=>{
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }

    const handleMakeList = ()=>{
        editor.update(()=>{
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const command = isNumberedList
              ? INSERT_ORDERED_LIST_COMMAND
              : INSERT_UNORDERED_LIST_COMMAND;


            // Dispatch the list command to finalize the change
            editor.dispatchCommand(command, undefined);
          }
        })
    }

      return (
      <button
          type='button'
          onClick={isActive ? handleRemoveList : handleMakeList}
          className={'m-2 hover-outline rounded-xl ' + (isActive ? 'bg-accent-300' : '')}
          aria-label="Format Bold">
              {Icon()}
        </button>
        )
  }

function Divider() {
  return <div className='w-[1px]  min-h-[70%] bg-divider-100' />;
}

export default function ToolbarPlugin({ hasClearOption,onSaveTask, hasSaveOption, clearPreset, errors}) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);

  const [isLeftAligned, setIsLeftAligned] = useState(false);
  const [isCenterAligned, setIsCenterAligned] = useState(false);
  const [isRightAligned, setIsRightAligned] = useState(false);

  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isBulletList, setIsBulletList] = useState(false);
  const [isNumberedList, setIsNumberedList] = useState(false);

  const [isLink, setIsLink] = useState(false);

  const [formOpen,setFormOpen] = useState(false);
  const [title,setTitle] = useState("");
  const [level,setLevel] = useState("");


  const handleClearEditor = () => {
      editor.update(() => {
      const root = $getRoot();
      root.clear(); // Clear all content
      });
      clearPreset(false)
  };
  
  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      const alignedParent = selection.getNodes()[0]?.getParent();
      const alignment = alignedParent?.getFormatType('align'); // `align` key depends on your implementation
      
      setIsLeftAligned(alignment === 'left'); // Default alignment is usually `left`
      setIsCenterAligned(alignment === 'center');
      setIsRightAligned(alignment === 'right');

      const anchorNode = selection.anchor.getNode();
      const parentNode = anchorNode.getParent();
      const desiredParent = parentNode?.getParent();
      const isListItemNode = $isListItemNode(parentNode);
      const isListNode = $isListNode(parentNode);
        
      setIsBulletList((isListItemNode || isListNode) && (desiredParent.__tag === 'ul' || parentNode.__tag === 'ul'));
      setIsNumberedList((isListNode || isListItemNode) && (desiredParent.__tag === 'ol' || parentNode.__tag === 'ol'));

      setIsLink($isLinkNode(parentNode));
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({editorState}) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, $updateToolbar]);

  const handleSaveTask = () => {
    if(!level || !title){
      showErrorToast("Error",'Please enter a valid title and job level to create a task template.')
      return
    }
    setFormOpen(false)
    onSaveTask(title,level)
  }

  return (
    <div className={"absolute top-0 left-0 min-h-14 z-20 flex items-center bg-background-80 rounded-t-xl   " + (errors ? "w-[calc(100%-2px)] ml-[1px] mt-[1px]" : "w-full")} ref={toolbarRef}>
      {/* <button
        type='button'
        disabled={!canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        className="toolbar-item spaced"
        aria-label="Undo">
        Undo
      </button>
      <button
        type='button'
        disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        className="toolbar-item"
        aria-label="Redo">
        Redo
      </button>
      <Divider /> */}
      <ToolButton Icon={()=><IconWrapper icon={Bold} size={3} customStrokeWidth={5} />} command={FORMAT_TEXT_COMMAND} commandType={'bold'} isActive={isBold} />
      <ToolButton Icon={()=><IconWrapper icon={Italic} size={3} customStrokeWidth={5} />} command={FORMAT_TEXT_COMMAND} commandType={'italic'} isActive={isItalic} />
      <ToolButton Icon={()=><IconWrapper icon={Underline} size={3} customStrokeWidth={5} />} command={FORMAT_TEXT_COMMAND} commandType={'underline'} isActive={isUnderline} />
      {/* <ToolButton Icon={()=><div>S</div>} command={FORMAT_TEXT_COMMAND} commandType={'strikethrough'} isActive={isStrikethrough} /> */}
      <Divider />
      <ToolButton Icon={()=><IconWrapper icon={AlignLeft} size={3} customStrokeWidth={5} />} command={FORMAT_ELEMENT_COMMAND} commandType={'left'} isActive={isLeftAligned} />
      <ToolButton Icon={()=><IconWrapper icon={AlignCenter} size={3} customStrokeWidth={5} />} command={FORMAT_ELEMENT_COMMAND} commandType={'center'} isActive={isCenterAligned} />
      <ToolButton Icon={()=><IconWrapper icon={AlignRight} size={3} customStrokeWidth={5} />} command={FORMAT_ELEMENT_COMMAND} commandType={'right'} isActive={isRightAligned} />
      {/* <ToolButton Icon={()=><div>J</div>} command={FORMAT_ELEMENT_COMMAND} commandType={'justify'} isActive={isUnderline} /> */}
      <Divider />
      <ListToolButton Icon={()=><IconWrapper icon={List} size={3} customStrokeWidth={5} />} isNumberedList={false} isActive={isBulletList} />
      <ListToolButton Icon={()=><IconWrapper icon={ListOrdered} size={3} customStrokeWidth={5} />} isNumberedList={true} isActive={isNumberedList} />
      <Divider />
      <EmojiPlugin/>
      <Divider />
      <LinkPlugin isActive={isLink}  />
      {hasSaveOption && <div className='absolute top-4 right-16 z-20 cursor-pointer' onClick={() => setFormOpen(true)}>
          <CustomToolTip arrowed title={'Save as Preset'}>
              <IconWrapper icon={Plus} customIconSize={2} customStrokeWidth={5} size={0} />
          </CustomToolTip>
      </div>}
      {hasClearOption && 
      <div className='absolute top-4 right-4 z-20 cursor-pointer' onClick={handleClearEditor}>
          <IconWrapper icon={Eraser} customIconSize={2} customStrokeWidth={4} size={0} />
      </div>}
      <Modal 
      open={formOpen}
      isReadyToClose={false}
      onClose={() => setFormOpen(false)}
      onConfirm={handleSaveTask}
      customMessage={'Save your drafted Design Task as a template for future use.'}
      customTitle={'Save Task as Template'}
      customConfirmLabel={'Save'}
      >
        <div className='mt-6 flex flex-col gap-4' action="">
              <InputField
                type="text"
                id="title"
                label="Title"
                value={title ?? ""}
                onChange={(e) => setTitle(e.target.value)}
                // error={error}
                // errorMessage={error?.message}
              />
              <GlobalDropDown
              label="Level" 
              value={level}
              // error={industryError ? {message : industryError} : null}
              onChange={setLevel}
              options={[{label : 'Junior' , value : 'Junior'},{label : 'Mid-Level' , value : 'Mid-Level'},{label : 'Senior' , value : 'Senior'}]}
              />
        </div>
      </Modal>
    </div>
  );
}